import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BatteryChargingIcon, SaveIcon, XIcon, EditIcon, Trash2Icon, PlusIcon, PackageIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { collection, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
interface ChargerPrices {
  'Zaptec 1': number;
  'Zaptec 2': number;
  Andet: number;
}
interface ChargerSettings {
  prices: ChargerPrices;
  averageSalePrice?: number;
}
interface ChargerMaterial {
  name: string;
  price: number;
  quantity: number;
}
export interface StandardMaterial {
  id: string;
  name: string;
  price?: number;
}
const defaultChargerMaterials: ChargerMaterial[] = [{
  name: 'Kombiafbr.',
  price: 465.11,
  quantity: 1
}, {
  name: 'Ladeboks',
  price: 1600.0,
  quantity: 1
}, {
  name: 'Kabel 5g4',
  price: 432.16,
  quantity: 20
}, {
  name: 'Tavle UG 150',
  price: 71.43,
  quantity: 1
}, {
  name: 'Diverse små ting',
  price: 240.0,
  quantity: 1
}, {
  name: 'ELCON Timeløn',
  price: 545.0,
  quantity: 1
}];
export function ChargerSettings() {
  const [prices, setPrices] = useState<ChargerPrices>({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  const [averageSalePrice, setAverageSalePrice] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMaterials, setIsEditingMaterials] = useState(false);
  const [tempPrices, setTempPrices] = useState<ChargerPrices>(prices);
  const [tempAverageSalePrice, setTempAverageSalePrice] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Standard Materials state
  const [standardMaterials, setStandardMaterials] = useState<StandardMaterial[]>([]);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [newMaterialPrice, setNewMaterialPrice] = useState('');
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [editMaterialName, setEditMaterialName] = useState('');
  const [editMaterialPrice, setEditMaterialPrice] = useState('');
  // Materials state
  const [chargerMaterials, setChargerMaterials] = useState<ChargerMaterial[]>(() => {
    const saved = localStorage.getItem('chargerMaterials');
    return saved ? JSON.parse(saved) : defaultChargerMaterials;
  });
  const [installationPrice, setInstallationPrice] = useState(() => {
    const saved = localStorage.getItem('installationPrice');
    return saved ? parseFloat(saved) : 4400.0;
  });
  // Auto-save materials to localStorage
  useEffect(() => {
    localStorage.setItem('chargerMaterials', JSON.stringify(chargerMaterials));
  }, [chargerMaterials]);
  useEffect(() => {
    localStorage.setItem('installationPrice', installationPrice.toString());
  }, [installationPrice]);
  // Load standard materials from Firebase with real-time sync
  useEffect(() => {
    const docRef = doc(db, 'settings', 'standardMaterials');
    const unsubscribe = onSnapshot(docRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setStandardMaterials(data.items || []);
      } else {
        // Initialize if it doesn't exist
        setDoc(docRef, {
          items: []
        });
        setStandardMaterials([]);
      }
    }, error => {
      console.error('Error loading standard materials:', error);
    });
    return () => unsubscribe();
  }, []);
  // Load prices from Firebase on mount
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Support both old format (just prices) and new format (with averageSalePrice)
          if (data.prices) {
            // New format
            const loadedPrices = data.prices as ChargerPrices;
            const loadedAverage = data.averageSalePrice || 0;
            setPrices(loadedPrices);
            setAverageSalePrice(loadedAverage);
            setTempPrices(loadedPrices);
            setTempAverageSalePrice(loadedAverage);
          } else {
            // Old format - migrate to new format
            const loadedPrices = data as ChargerPrices;
            setPrices(loadedPrices);
            setAverageSalePrice(0);
            setTempPrices(loadedPrices);
            setTempAverageSalePrice(0);
          }
        } else {
          // Initialize with default prices if not exists
          const defaultPrices: ChargerPrices = {
            'Zaptec 1': 1046.3,
            'Zaptec 2': 4321.54,
            Andet: 0
          };
          await setDoc(docRef, {
            prices: defaultPrices,
            averageSalePrice: 0
          });
          setPrices(defaultPrices);
          setAverageSalePrice(0);
          setTempPrices(defaultPrices);
          setTempAverageSalePrice(0);
        }
      } catch (error) {
        console.error('Error loading charger prices:', error);
        alert('Fejl ved indlæsning af priser. Tjek konsollen for detaljer.');
      } finally {
        setIsLoading(false);
      }
    };
    loadPrices();
  }, []);
  const saveStandardMaterialsToDb = async (items: StandardMaterial[]) => {
    try {
      const docRef = doc(db, 'settings', 'standardMaterials');
      await setDoc(docRef, {
        items
      });
    } catch (error) {
      console.error('Error saving standard materials:', error);
      alert('Fejl ved gemning af standard materialer.');
    }
  };
  const handleAddStandardMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMaterialName.trim()) return;
    const newMaterial: StandardMaterial = {
      id: Date.now().toString(),
      name: newMaterialName.trim(),
      price: newMaterialPrice ? parseFloat(newMaterialPrice) : undefined
    };
    const updated = [...standardMaterials, newMaterial].sort((a, b) => a.name.localeCompare(b.name));
    await saveStandardMaterialsToDb(updated);
    setNewMaterialName('');
    setNewMaterialPrice('');
  };
  const startEditStandardMaterial = (material: StandardMaterial) => {
    setEditingMaterialId(material.id);
    setEditMaterialName(material.name);
    setEditMaterialPrice(material.price?.toString() || '');
  };
  const handleSaveStandardMaterial = async (id: string) => {
    if (!editMaterialName.trim()) return;
    const updated = standardMaterials.map(m => {
      if (m.id === id) {
        return {
          ...m,
          name: editMaterialName.trim(),
          price: editMaterialPrice ? parseFloat(editMaterialPrice) : undefined
        };
      }
      return m;
    }).sort((a, b) => a.name.localeCompare(b.name));
    await saveStandardMaterialsToDb(updated);
    setEditingMaterialId(null);
  };
  const handleDeleteStandardMaterial = async (id: string) => {
    if (confirm('Er du sikker på, at du vil slette dette materiale?')) {
      const updated = standardMaterials.filter(m => m.id !== id);
      await saveStandardMaterialsToDb(updated);
    }
  };
  const handleEdit = () => {
    setTempPrices(prices);
    setTempAverageSalePrice(averageSalePrice);
    setIsEditing(true);
  };
  const handleCancel = () => {
    setTempPrices(prices);
    setTempAverageSalePrice(averageSalePrice);
    setIsEditing(false);
  };
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const docRef = doc(db, 'settings', 'chargerPrices');
      await setDoc(docRef, {
        prices: tempPrices,
        averageSalePrice: tempAverageSalePrice
      });
      setPrices(tempPrices);
      setAverageSalePrice(tempAverageSalePrice);
      setIsEditing(false);
      // Show success message
      alert('Indstillinger gemt! Alle brugere vil nu se de opdaterede værdier.');
    } catch (error) {
      console.error('Error saving charger settings:', error);
      alert('Fejl ved gemning af indstillinger. Tjek konsollen for detaljer.');
    } finally {
      setIsSaving(false);
    }
  };
  const updatePrice = (type: keyof ChargerPrices, value: string) => {
    setTempPrices({
      ...tempPrices,
      [type]: parseFloat(value) || 0
    });
  };
  // Calculate charger profit
  const materialsCost = useMemo(() => {
    return chargerMaterials.reduce((sum, material) => sum + material.price * material.quantity, 0);
  }, [chargerMaterials]);
  const chargerProfit = installationPrice - materialsCost;
  const handleSaveMaterials = () => {
    setIsEditingMaterials(false);
  };
  const handleCancelMaterialsEdit = () => {
    const saved = localStorage.getItem('chargerMaterials');
    setChargerMaterials(saved ? JSON.parse(saved) : defaultChargerMaterials);
    const savedPrice = localStorage.getItem('installationPrice');
    setInstallationPrice(savedPrice ? parseFloat(savedPrice) : 4400.0);
    setIsEditingMaterials(false);
  };
  const updateMaterial = (index: number, field: keyof ChargerMaterial, value: string | number) => {
    const updated = [...chargerMaterials];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setChargerMaterials(updated);
  };
  if (isLoading) {
    return <div className="space-y-6" data-id="element-1334">
        <header className="mb-8" data-id="element-1335">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1336">
            Ladeboks information
          </h1>
          <p className="text-stone-600" data-id="element-1337">Indlæser indstillinger...</p>
        </header>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1338">
      <header className="mb-8" data-id="element-1339">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1340">
          Ladeboks information
        </h1>
        <p className="text-stone-600" data-id="element-1341">
          Administrer materialepriser og gennemsnitlige salgspriser for
          ladebokse
        </p>
      </header>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1342">
        <div className="flex items-center justify-between mb-6" data-id="element-1343">
          <div className="flex items-center gap-3" data-id="element-1344">
            <div className="p-3 bg-teal-100 rounded-xl" data-id="element-1345">
              <BatteryChargingIcon className="w-6 h-6 text-teal-700" data-id="element-1346" />
            </div>
            <div data-id="element-1347">
              <h2 className="text-lg font-semibold text-stone-900" data-id="element-1348">
                Ladeboks materialepriser
              </h2>
              <p className="text-sm text-stone-500" data-id="element-1349">
                Priser bruges automatisk ved oprettelse af opgaver
              </p>
            </div>
          </div>

          {!isEditing ? <Button onClick={handleEdit} variant="primary" data-id="element-1350">
              Rediger indstillinger
            </Button> : <div className="flex gap-2" data-id="element-1351">
              <Button onClick={handleCancel} variant="secondary" className="flex items-center gap-2" disabled={isSaving} data-id="element-1352">
                <XIcon className="w-4 h-4" data-id="element-1353" />
                Annuller
              </Button>
              <Button onClick={handleSave} variant="primary" className="flex items-center gap-2" disabled={isSaving} data-id="element-1354">
                <SaveIcon className="w-4 h-4" data-id="element-1355" />
                {isSaving ? 'Gemmer...' : 'Gem ændringer'}
              </Button>
            </div>}
        </div>

        <div className="space-y-4" data-id="element-1356">
          {/* Zaptec 1 */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1357">
            <div className="flex items-center justify-between" data-id="element-1358">
              <div className="flex-1" data-id="element-1359">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1360">Zaptec 1</h3>
                <p className="text-sm text-stone-500" data-id="element-1361">Standard ladeboks type</p>
              </div>
              <div className="w-48" data-id="element-1362">
                {isEditing ? <Input label="Pris (kr)" type="number" step="0.01" value={tempPrices['Zaptec 1']} onChange={e => updatePrice('Zaptec 1', e.target.value)} data-id="element-1363" /> : <div className="text-right" data-id="element-1364">
                    <p className="text-sm text-stone-500" data-id="element-1365">Materialer pris</p>
                    <p className="text-2xl font-bold text-stone-900" data-id="element-1366">
                      {prices['Zaptec 1'].toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Zaptec 2 */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1367">
            <div className="flex items-center justify-between" data-id="element-1368">
              <div className="flex-1" data-id="element-1369">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1370">Zaptec 2</h3>
                <p className="text-sm text-stone-500" data-id="element-1371">Premium ladeboks type</p>
              </div>
              <div className="w-48" data-id="element-1372">
                {isEditing ? <Input label="Pris (kr)" type="number" step="0.01" value={tempPrices['Zaptec 2']} onChange={e => updatePrice('Zaptec 2', e.target.value)} data-id="element-1373" /> : <div className="text-right" data-id="element-1374">
                    <p className="text-sm text-stone-500" data-id="element-1375">Materialer pris</p>
                    <p className="text-2xl font-bold text-stone-900" data-id="element-1376">
                      {prices['Zaptec 2'].toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Andet */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1377">
            <div className="flex items-center justify-between" data-id="element-1378">
              <div className="flex-1" data-id="element-1379">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1380">Andet</h3>
                <p className="text-sm text-stone-500" data-id="element-1381">
                  Manuel indtastning af pris
                </p>
              </div>
              <div className="w-48" data-id="element-1382">
                <div className="text-right" data-id="element-1383">
                  <p className="text-sm text-stone-500" data-id="element-1384">Materialer pris</p>
                  <p className="text-lg font-medium text-stone-600" data-id="element-1385">
                    Manuel indtastning
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: Average Sale Price */}
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200" data-id="element-1386">
            <div className="flex items-center justify-between" data-id="element-1387">
              <div className="flex-1" data-id="element-1388">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1389">
                  Gennemsnit pr ladeboks salg
                </h3>
                <p className="text-sm text-stone-500" data-id="element-1390">
                  Gennemsnitlig salgspris for en ladeboks installation
                </p>
              </div>
              <div className="w-48" data-id="element-1391">
                {isEditing ? <Input label="Gennemsnitspris (kr)" type="number" step="0.01" value={tempAverageSalePrice} onChange={e => setTempAverageSalePrice(parseFloat(e.target.value) || 0)} data-id="element-1392" /> : <div className="text-right" data-id="element-1393">
                    <p className="text-sm text-stone-500" data-id="element-1394">Gennemsnitspris</p>
                    <p className="text-2xl font-bold text-amber-900" data-id="element-1395">
                      {averageSalePrice.toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1396">
          <p className="text-sm text-blue-900" data-id="element-1397">
            <strong data-id="element-1398">Bemærk:</strong> Når du vælger en ladeboks type ved
            oprettelse af en opgave, vil materialeprisen automatisk blive
            udfyldt med den pris du har angivet her. For "Andet" skal prisen
            indtastes manuelt.
          </p>
        </div>

        {/* Firebase sync info */}
        <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-1399">
          <p className="text-sm text-teal-900" data-id="element-1400">
            <strong data-id="element-1401">🔥 Firebase Sync:</strong> Alle ændringer gemmes i skyen og
            deles automatisk med alle brugere på tværs af alle computere og
            enheder.
          </p>
        </div>
      </motion.div>

      {/* Charger Profit Calculator */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm" data-id="element-1402">
        <div className="flex items-center justify-between mb-8" data-id="element-1403">
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-1404">
            Gennemsnit pr ladeboks salg
          </h2>
          {!isEditingMaterials ? <button onClick={() => setIsEditingMaterials(true)} className="flex items-center gap-2 px-4 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-1405">
              <EditIcon className="w-4 h-4" data-id="element-1406" />
              Rediger
            </button> : <div className="flex gap-2" data-id="element-1407">
              <button onClick={handleCancelMaterialsEdit} className="flex items-center gap-2 px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors" data-id="element-1408">
                <XIcon className="w-4 h-4" data-id="element-1409" />
                Annuller
              </button>
              <button onClick={handleSaveMaterials} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg transition-colors" data-id="element-1410">
                <SaveIcon className="w-4 h-4" data-id="element-1411" />
                Gem
              </button>
            </div>}
        </div>

        <div className="overflow-x-auto" data-id="element-1412">
          <table className="w-full" data-id="element-1413">
            <thead data-id="element-1414">
              <tr className="border-b border-stone-200" data-id="element-1415">
                <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1416">
                  Materialer
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1417">
                  Pris
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1418">
                  Stk
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1419">
                  Total
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1420">
              {chargerMaterials.map((material, index) => <tr key={index} className="border-b border-stone-100" data-id="element-1421">
                  <td className="py-3 px-4" data-id="element-1422">
                    {isEditingMaterials ? <input type="text" value={material.name} onChange={e => updateMaterial(index, 'name', e.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded" data-id="element-1423" /> : <span className="text-stone-900" data-id="element-1424">{material.name}</span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1425">
                    {isEditingMaterials ? <input type="number" step="0.01" value={material.price} onChange={e => updateMaterial(index, 'price', parseFloat(e.target.value) || 0)} className="w-24 px-2 py-1 border border-stone-200 rounded text-right" data-id="element-1426" /> : <span className="text-stone-900" data-id="element-1427">
                        kr {material.price.toFixed(2)}
                      </span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1428">
                    {isEditingMaterials ? <input type="number" value={material.quantity} onChange={e => updateMaterial(index, 'quantity', parseInt(e.target.value) || 0)} className="w-16 px-2 py-1 border border-stone-200 rounded text-right" data-id="element-1429" /> : <span className="text-stone-900" data-id="element-1430">
                        {material.quantity}
                      </span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1431">
                    <span className="text-stone-900 font-medium" data-id="element-1432">
                      kr {(material.price * material.quantity).toFixed(2)}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot data-id="element-1433">
              <tr className="border-t-2 border-stone-300 font-semibold" data-id="element-1434">
                <td colSpan={3} className="py-4 px-4 text-stone-900" data-id="element-1435">
                  Pris (Installation + boks)
                </td>
                <td className="py-4 px-4 text-right" data-id="element-1436">
                  {isEditingMaterials ? <input type="number" step="0.01" value={installationPrice} onChange={e => setInstallationPrice(parseFloat(e.target.value) || 0)} className="w-32 px-2 py-1 border border-stone-200 rounded text-right font-semibold" data-id="element-1437" /> : <span className="text-stone-900" data-id="element-1438">
                      kr {installationPrice.toFixed(2)}
                    </span>}
                </td>
              </tr>
              <tr className="font-semibold" data-id="element-1439">
                <td colSpan={3} className="py-3 px-4 text-stone-900" data-id="element-1440">
                  Pris (Materialer)
                </td>
                <td className="py-3 px-4 text-right text-stone-900" data-id="element-1441">
                  -kr {materialsCost.toFixed(2)}
                </td>
              </tr>
              <tr className="border-t-2 border-stone-300 font-bold text-lg" data-id="element-1442">
                <td colSpan={3} className="py-4 px-4 text-emerald-700" data-id="element-1443">
                  Profit pr stk.
                </td>
                <td className="py-4 px-4 text-right text-emerald-700" data-id="element-1444">
                  kr {chargerProfit.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </motion.div>

      {/* Standard Materials Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm" data-id="element-1445">
        <div className="flex items-center gap-3 mb-6" data-id="element-1446">
          <div className="p-3 bg-teal-100 rounded-xl" data-id="element-1447">
            <PackageIcon className="w-6 h-6 text-teal-700" data-id="element-1448" />
          </div>
          <div data-id="element-1449">
            <h2 className="text-lg font-semibold text-stone-900" data-id="element-1450">
              Standard materialer
            </h2>
            <p className="text-sm text-stone-500" data-id="element-1451">
              Materialer der hurtigt kan vælges når der oprettes opgaver
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-8" data-id="element-1452">
          {standardMaterials.length === 0 ? <div className="p-6 text-center border border-stone-200 border-dashed rounded-xl bg-stone-50" data-id="element-1453">
              <p className="text-sm text-stone-500" data-id="element-1454">
                Ingen standard materialer endnu.
              </p>
            </div> : standardMaterials.map(material => <div key={material.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1455">
                {editingMaterialId === material.id ? <div className="flex-1 flex items-center gap-3 mr-4" data-id="element-1456">
                    <input type="text" value={editMaterialName} onChange={e => setEditMaterialName(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Materiale navn" data-id="element-1457" />
                    <input type="number" step="0.01" value={editMaterialPrice} onChange={e => setEditMaterialPrice(e.target.value)} className="w-32 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Pris (valgfri)" data-id="element-1458" />
                  </div> : <div className="flex-1" data-id="element-1459">
                    <p className="font-medium text-stone-900" data-id="element-1460">
                      {material.name}
                    </p>
                    {material.price !== undefined && <p className="text-sm text-stone-500" data-id="element-1461">
                        {material.price.toLocaleString('da-DK')} kr
                      </p>}
                  </div>}

                <div className="flex items-center gap-2" data-id="element-1462">
                  {editingMaterialId === material.id ? <>
                      <button onClick={() => setEditingMaterialId(null)} className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-200 rounded-lg transition-colors" data-id="element-1463">
                        <XIcon className="w-4 h-4" data-id="element-1464" />
                      </button>
                      <button onClick={() => handleSaveStandardMaterial(material.id)} className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-1465">
                        <SaveIcon className="w-4 h-4" data-id="element-1466" />
                      </button>
                    </> : <>
                      <button onClick={() => startEditStandardMaterial(material)} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-1467">
                        <EditIcon className="w-4 h-4" data-id="element-1468" />
                      </button>
                      <button onClick={() => handleDeleteStandardMaterial(material.id)} className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1469">
                        <Trash2Icon className="w-4 h-4" data-id="element-1470" />
                      </button>
                    </>}
                </div>
              </div>)}
        </div>

        <form onSubmit={handleAddStandardMaterial} className="p-5 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1471">
          <h3 className="text-sm font-semibold text-stone-900 mb-3" data-id="element-1472">
            Tilføj nyt standard materiale
          </h3>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1473">
            <input type="text" value={newMaterialName} onChange={e => setNewMaterialName(e.target.value)} placeholder="Materiale navn..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" required data-id="element-1474" />
            <input type="number" step="0.01" value={newMaterialPrice} onChange={e => setNewMaterialPrice(e.target.value)} placeholder="Pris (valgfri)" className="w-full sm:w-40 px-4 py-2.5 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" data-id="element-1475" />
            <Button type="submit" variant="primary" className="flex items-center justify-center gap-2" data-id="element-1476">
              <PlusIcon className="w-4 h-4" data-id="element-1477" />
              Tilføj
            </Button>
          </div>
        </form>
      </motion.div>
    </div>;
}